import { useEffect, useRef, useState, useCallback } from "react";
import { Layers, Upload, Loader2, CheckCircle, AlertTriangle, ArrowRight, Images } from "lucide-react";
import { toast } from "sonner";
import CockpitLayout from "@/components/admin/CockpitLayout";
import { GoldAction } from "@/components/admin/GoldUi";
import { api } from "@/lib/api";

export default function Batch() {
  const [files, setFiles] = useState([]);
  const [batch, setBatch] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [recent, setRecent] = useState([]);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const loadRecent = useCallback(() => { api.get("/batch").then(({ data }) => setRecent(data)).catch(() => {}); }, []);
  useEffect(() => { loadRecent(); return () => clearInterval(pollRef.current); }, [loadRecent]);

  const poll = useCallback((id) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/batch/${id}`);
        setBatch(data);
        if (data.status === "done") {
          clearInterval(pollRef.current);
          toast.success(`Fertig! ${data.created_item_ids.length} Entwürfe erstellt.`);
          loadRecent();
        }
      } catch { clearInterval(pollRef.current); }
    }, 2000);
  }, [loadRecent]);

  const start = async () => {
    if (!files.length) { toast.error("Bitte zuerst Fotos auswählen."); return; }
    setUploading(true);
    setBatch(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/batch", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBatch({ ...data, processed: 0, failed: 0, created_item_ids: [] });
      setFiles([]);
      if (fileRef.current) fileRef.current.value = "";
      poll(data.id);
      toast.info("Verarbeitung gestartet – die KI erstellt Entwürfe.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload fehlgeschlagen.");
    } finally { setUploading(false); }
  };

  const pct = batch ? Math.round((batch.processed / batch.total) * 100) : 0;

  return (
    <CockpitLayout active="fundstuecke" title="Batch-Upload">
      <div className="fundio-page-stack" data-testid="gold-batch">
        <div>
          <h1 className="fundio-page-title flex items-center gap-2"><Layers size={26} aria-hidden="true" /> Batch-Upload</h1>
          <p className="mt-2 fundio-muted">Lade bis zu 40 Fotos auf einmal hoch – die KI erstellt für jedes Bild automatisch einen Entwurf.</p>
        </div>

        <div className="fundio-panel">
          <div className="fundio-upload-zone">
            <Images aria-hidden="true" size={28} />
            <p className="fundio-muted">{files.length ? `${files.length} Fotos ausgewählt` : "Mehrere Fotos auswählen"}</p>
            <div className="fundio-upload-actions">
              <GoldAction data-testid="batch-select-btn" onClick={() => fileRef.current?.click()}><Upload size={18} aria-hidden="true" /> Fotos auswählen</GoldAction>
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => setFiles(Array.from(e.target.files || []))} />
          </div>
          <GoldAction variant="gold" full className="mt-4" data-testid="batch-start-btn" onClick={start} disabled={uploading || !files.length}>
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Layers size={16} aria-hidden="true" />} {files.length ? `${files.length} Fotos analysieren` : "Analysieren"}
          </GoldAction>
        </div>

        {batch && (
          <div className="fundio-panel" data-testid="batch-progress">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2" style={{ fontWeight: 700 }}>{batch.status === "done" ? <CheckCircle size={18} style={{ color: "#1E7E45" }} aria-hidden="true" /> : <Loader2 size={18} className="animate-spin" aria-hidden="true" />} {batch.status === "done" ? "Abgeschlossen" : "Verarbeitung läuft …"}</span>
              <span className="fundio-muted">{batch.processed}/{batch.total}</span>
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-full" style={{ background: "#EFEDE6" }}><div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: "var(--f-gold)" }} /></div>
            {batch.failed > 0 && <p className="mt-3 flex items-center gap-1.5" style={{ color: "#8C5A00", fontSize: 14 }}><AlertTriangle size={14} aria-hidden="true" /> {batch.failed} Bild(er) konnten nicht verarbeitet werden.</p>}
            {batch.status === "done" && (
              <GoldAction to="/dashboard/fundstuecke?tab=draft" variant="primary" className="mt-4">
                {batch.created_item_ids.length} Entwürfe prüfen <ArrowRight size={16} aria-hidden="true" />
              </GoldAction>
            )}
          </div>
        )}

        {recent.length > 0 && (
          <section className="fundio-panel">
            <h2 className="fundio-section-title">Letzte Durchgänge</h2>
            <div className="mt-3 flex flex-col gap-2">
              {recent.map((b) => (
                <div key={b.id} className="flex items-center justify-between" style={{ borderTop: "1px solid var(--f-line)", paddingTop: 12 }}>
                  <span className="fundio-muted" style={{ fontSize: 14 }}>{new Date(b.created_at).toLocaleString("de-CH")}</span>
                  <span className="flex items-center gap-3">
                    <span className="fundio-muted" style={{ fontSize: 14 }}>{b.created_item_ids?.length || 0} Entwürfe</span>
                    <span className="fundio-status" style={b.status === "done" ? { background: "#E7F6EC", color: "#1E7E45", borderColor: "#B6E0C5" } : undefined}>{b.status === "done" ? "Fertig" : "Läuft"}</span>
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </CockpitLayout>
  );
}
